import {
  KdsButton,
  KdsClipboardButton,
  KdsEmptyContent,
  KdsHeader,
  KdsText,
} from "@kazaar-platform/design-system";
import { RiAddFill } from "@remixicon/react";
import { T, useTranslate } from "@tolgee/react";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";
import { isEmpty, isNil } from "lodash";
import { useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { CardQuote } from "@/components/CardQuote";
import { FileContextProvider } from "@/components/FileUploadAsync/FileUploadAsync.context";
import { PageTemplate } from "@/components/PageTemplate";
import { useOrderItemQuotes, useOrderItems, useToasts } from "@/hooks";
import { ModalAddQuote } from "@/modals/ModalAddQuote";
import { currentWorkspaceAtom, userAtom } from "@/store";

export const OrderDetailsAddQuoteFormPage = () => {
  // Hooks
  const { t } = useTranslate();
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);
  const { add } = useToasts();
  const navigate = useNavigate();
  const userAtomValue = useAtomValue(userAtom);
  const { orderId, orderItemId } = useParams() as {
    orderId: string;
    orderItemId: string;
  };

  // States
  const [isAddQuoteModalOpen, setIsAddQuoteModalOpen] =
    useState<boolean>(false);
  const [quoteIdToDelete, setQuoteIdToDelete] = useState<string | undefined>(
    undefined
  );

  // Query hooks
  const { useGetOrderItem } = useOrderItems();
  const {
    useSendOrderItemQuotes,
    useDeleteOrderItemQuote: useDeleteSandboxItemQuote,
  } = useOrderItemQuotes();
  const getOrderItemDetailsQueryResult = useGetOrderItem({
    orderId: orderId,
    orderItemId: orderItemId,
  });
  const { mutateAsync: sendSandboxItemQuotes, isPending: isPendingSendQuotes } =
    useSendOrderItemQuotes();
  const { mutateAsync: deleteSandboxItemQuotes } = useDeleteSandboxItemQuote();

  // Get OrderItem
  const orderItem = getOrderItemDetailsQueryResult.data?.data?.orderItem;

  // OrderItem Loading State
  const isLoading = getOrderItemDetailsQueryResult.isLoading;

  // Conditions
  const hasQuotesToSend = !isEmpty(
    orderItem?.sandboxItem?.SandboxItemQuote.filter((quote) => !quote.sentAt)
  );

  // Define TotalQuantity
  const quantity = orderItem?.sandboxItem?.SandboxItemVersion?.reduce(
    (sumByVersion, version) =>
      sumByVersion +
      version?.SandboxItemVersionDispatch?.reduce(
        (sumByAddress, address) => sumByAddress + address?.quantity,
        0
      ),
    0
  );

  // Define QuotesToSend Array
  const quotesToSend = useMemo(
    () =>
      orderItem?.sandboxItem?.SandboxItemQuote.filter(
        (quote) => !quote.sentAt
      ).sort((a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix()),
    [orderItem]
  );

  // 404 Redirection if quote already send OR validated
  if (orderItem?.sandboxItemQuoteId) {
    return <Navigate to="/404" />;
  }

  // Errors (not 404)
  if (isNil(orderItem) || isEmpty(orderItem)) {
    return null;
  }

  // Handle Delete Quote
  const onDeleteQuote = async (quoteId: string) => {
    const res = await deleteSandboxItemQuotes({
      sandboxItemQuoteId: quoteId,
      orderId: orderItem.orderId,
      orderItemId: orderItemId,
    });

    if (res.status === 200 || res.status === 201) {
      add({
        state: "success",
        title: t("oms.addQuote.toastDeleteSuccess"),
      });
    } else {
      // Display error toast
      add({
        state: "error",
        title: t("toast.form.error"),
      });
    }
  };

  // Handle Submit
  const onSubmit = async () => {
    const res = await sendSandboxItemQuotes({
      command: {
        sandboxItemsQuotesIds: orderItem.sandboxItem.SandboxItemQuote.map(
          (sq) => sq.id
        ),
      },
      orderId: orderItem.orderId,
      orderItemId: orderItem.id,
    });

    if (res.status === 200 || res.status === 201) {
      void navigate(`/orders/${orderId}/`);
      add({
        state: "success",
        title: t("oms.addQuote.toastSendSuccess"),
      });
    } else {
      // Display error toast
      add({
        state: "error",
        title: t("toast.form.error"),
      });
    }
  };

  return (
    <FileContextProvider>
      <PageTemplate>
        <PageTemplate.Header>
          <KdsHeader
            backButtonProps={{ onPress: () => void navigate(-1) }}
            buttons={[
              {
                buttonType: "button",
                color: "neutral",
                variant: "outlined",
                children: t("buttons.addQuote"),
                dataCy: "openQuoteModal",
                iconLeft: RiAddFill,
                size: "small",
                onPress: () => setIsAddQuoteModalOpen(true),
              },
              {
                buttonType: "button",
                disabled: !hasQuotesToSend,
                isLoading: isPendingSendQuotes,
                dataCy: "sendQuoteToClient",
                children: t("buttons.sendToClient"),
                size: "small",
                onPress: () => void onSubmit(),
              },
            ]}
            isLoading={isLoading}
            metadatas={
              orderItem.order.referenceId
                ? [
                    <KdsClipboardButton
                      key={orderItem.order.referenceId}
                      text={orderItem.order.referenceId}
                      alt={t("clipboard.orderId")}
                      displayToast={() =>
                        add({
                          key: orderItem.order.referenceId,
                          state: "reverse",
                          title: t("toast.clipboard.orderId"),
                        })
                      }
                    />,
                  ]
                : []
            }
            title={orderItem?.sandboxItem?.name}
          />
        </PageTemplate.Header>

        <PageTemplate.Content className="pb-22 relative h-fit flex-col gap-6">
          <div className="rounded-2xl bg-white p-6">
            <div>
              <KdsText as="h6" size="title-md">
                {t("oms.addQuote.title")}
              </KdsText>
              <KdsText>{t("oms.addQuote.subtitle")}</KdsText>
            </div>

            {!hasQuotesToSend ? (
              <KdsEmptyContent
                title={t("oms.addQuote.emptyState.title")}
                description={t("oms.addQuote.emptyState.subtitle")}
              />
            ) : (
              <div className="mt-6 grid grid-cols-2 gap-6">
                {quotesToSend?.map((quote, index) => {
                  const relatedItemNames = quote.SandboxItem?.map(
                    (si) => si.name ?? ""
                  );
                  const totalItemRelatedToTheQuote = relatedItemNames.length;

                  return (
                    <div key={quote.id} className="relative">
                      <CardQuote
                        isNotSelectable
                        key={quote.id}
                        supplier={t("cardQuote.supplier", {
                          number: quotesToSend.length - index,
                        })}
                        quoteNumber={t("cardQuote.quoteNumber", {
                          number: quote.quoteNumber,
                        })}
                        quantity={quantity}
                        totalPrice={quote.totalPrice}
                        currency={currentWorkspace.currency}
                        locale={userAtomValue?.preferredLanguage}
                        fileUrl={quote.file.url}
                        deleteLabel={t("buttons.deleteQuote")}
                        onDelete={() => setQuoteIdToDelete(quote.id)}
                        className="flex flex-1"
                        itemsRelated={relatedItemNames}
                        itemsRelatedLabel={t("cardQuote.itemsRelatedLabel", {
                          count: totalItemRelatedToTheQuote,
                        })}
                        unitPriceLabel={t("cardQuote.unitPrice")}
                        totalPriceLabel={t("cardQuote.totalPrice")}
                        multiItemsLabel={t("cardQuote.labelMultiProduct")}
                        modalProps={{
                          title: t("cardQuote.modal.title"),
                          closeLabel: t("buttons.close"),
                        }}
                      />

                      {quoteIdToDelete === quote.id && (
                        <div className="absolute left-0 top-0 z-50 flex h-full w-full animate-fade-in flex-col justify-center gap-4 rounded-2xl bg-slate-50 p-6">
                          <div className="w-full text-center">
                            <KdsText>
                              <T
                                keyName="oms.addQuote.deleteQuoteConfirmationMessage"
                                params={{ b: <strong /> }}
                              />
                            </KdsText>
                          </div>

                          <div className="flex flex-row gap-4">
                            <div className="flex-1">
                              <KdsButton
                                variant="outlined"
                                color="neutral"
                                fullWidth
                                onPress={() => setQuoteIdToDelete(undefined)}
                              >
                                {t("buttons.cancel")}
                              </KdsButton>
                            </div>
                            <div className="flex-1">
                              <KdsButton
                                variant="contained"
                                color="error"
                                fullWidth
                                onPress={() => void onDeleteQuote(quote.id)}
                              >
                                {t("buttons.deleteQuote")}
                              </KdsButton>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </PageTemplate.Content>

        {/* Add Quote Modal */}
        {isAddQuoteModalOpen && (
          <ModalAddQuote
            orderItem={orderItem}
            onCloseModal={() => setIsAddQuoteModalOpen(false)}
          />
        )}
      </PageTemplate>
    </FileContextProvider>
  );
};
